import moment from 'moment';
import * as Yup from 'yup';

import { MAX_CARD_NUMBER_LENGTH, MIN_CARD_NUMBER_LENGTH } from 'constants/cards';

import { clearPhoneMask } from '../utils';
import { luhnAlgorithm } from '../utils/validate-card';

import { useTranslate } from './useTranslate';

declare module 'yup' {
  interface StringSchema {
    luhnAlgorithm(msg?: string): this;
    lengthCardNum(msg?: string): this;
    formatCardDate(msg?: string): this;
    expiryCardDate(msg?: string): this;
  }
}

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  'lengthCardNum',
  function lengthCardNum(message: string) {
    return this.test('lengthCardNum', message, (value) => {
      if (!value) return false;

      const clearValue = value.replace(/\s/g, '');

      if (
        clearValue.length < MIN_CARD_NUMBER_LENGTH ||
        clearValue.length > MAX_CARD_NUMBER_LENGTH
      ) {
        return false;
      }

      return true;
    });
  },
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  'luhnAlgorithm',
  function luhnAlgorithmCheck(message: string) {
    return this.test('luhnAlgorithm', message, (value) => {
      if (!value) return true;

      return luhnAlgorithm(value);
    });
  },
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  'formatCardDate',
  function formatCardDate(message: string) {
    return this.test('formatCardDate', message, (value) => {
      if (!value) return true;

      const expiryDate = moment(value, 'MM/YY', true);

      if (!expiryDate.isValid()) {
        return false;
      }

      return true;
    });
  },
);

Yup.addMethod<Yup.StringSchema>(
  Yup.string,
  'expiryCardDate',
  function expiryCardDate(message: string) {
    return this.test('expiryCardDate', message, (value) => {
      if (!value) return true;

      const currentDate = moment();
      const expiryDate = moment(value, 'MM/YY', true);

      if (expiryDate.isBefore(currentDate, 'month')) {
        return false;
      }

      return true;
    });
  },
);

const useValidate = () => {
  const { t } = useTranslate();

  const yupObjectCardValidate = {
    cardNumber: Yup.string()
      .required(t('wrong_card_number'))
      .lengthCardNum(t('wrong_card_number'))
      .luhnAlgorithm(t('wrong_card_number')),
    validity: Yup.string()
      .required(t('wrong_date_format'))
      .formatCardDate(t('wrong_date_format'))
      .expiryCardDate(t('wrong_date_format')),
    cvv: Yup.string().required(t('wrong_cvv')).length(3, t('wrong_cvv')),
    cardholder: Yup.string()
      .required(t('wrong_cardholder'))
      .min(2, t('wrong_cardholder'))
      .matches(/^[a-zA-Z\s-]+$/, t('wrong_cardholder')),
  };

  const validateCardNumber = (value: string): boolean | string => {
    const clearValue = value.replace(/\s/g, '');

    if (clearValue.length < MIN_CARD_NUMBER_LENGTH || clearValue.length > MAX_CARD_NUMBER_LENGTH) {
      return t('wrong_card_number');
    }

    if (!luhnAlgorithm(value)) {
      return t('wrong_card_number');
    }

    return true;
  };

  const validateCardDate = (value: string): boolean | string => {
    const currentDate = moment();
    const expiryDate = moment(value, 'MM/YY', true);

    if (!expiryDate.isValid()) {
      return t('wrong_date_format');
    }

    if (expiryDate.isBefore(currentDate, 'month')) {
      return t('wrong_date_format');
    }

    return true;
  };

  const validateCardMoth = (value: string): boolean | string => {
    const expiryDate = moment(value, 'MM', true);

    if (!expiryDate.isValid()) {
      return t('wrong_date_format');
    }

    return true;
  };

  const validateCardYear = (value: string): boolean | string => {
    const expiryDate = moment(value, 'YY', true);

    if (!expiryDate.isValid()) {
      return t('wrong_date_format');
    }

    return true;
  };

  const validatePlus24Login = (value: string): boolean | string => {
    const error = t('wrong_phone');
    const login = clearPhoneMask(value);

    if (!login) return error;

    const lengthValidation = (length: number) => login.length === length;

    const countryCodes = {
      '77': 11, // Kazakhstan
      '79': 11, // Russia
      '996': 12, // Kyrgyzstan
      '998': 12, // Uzbekistan
      '994': 12, // Azerbaijan
    };

    if (login.length < 11) return error;

    const countryCode = login.slice(0, 2) as keyof typeof countryCodes;

    if (countryCodes[countryCode] && !lengthValidation(countryCodes[countryCode])) {
      return error;
    }

    const internationalCode = login.slice(0, 3) as keyof typeof countryCodes;

    if (countryCodes[internationalCode] && !lengthValidation(countryCodes[internationalCode])) {
      return error;
    }

    return true;
  };

  const validateEmail = (value: string): boolean | string => {
    if (!value) {
      return t('enter_email_base');
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
      return t('wrong_email_base');
    }

    return true;
  };

  const validateAmount = (value: string, maxAmount: number, minAmount: number) => {
    const numberValue = Number(value);

    if (maxAmount < 1 || minAmount < 0) {
      return true;
    }

    if (numberValue > maxAmount) {
      return t('1xbet.amount.max', { amount: maxAmount });
    }

    if (numberValue < minAmount) {
      return t('1xbet.amount.min', { amount: minAmount });
    }

    return true;
  };

  return {
    yupObjectCardValidate,
    validateCardNumber,
    validateCardDate,
    validatePlus24Login,
    validateEmail,
    validateCardMoth,
    validateCardYear,
    validateAmount,
  };
};

export { useValidate };
